/**
 * Lease list — `GET tenants/leases/`, scoped to the caller. Activate/terminate
 * are the only status transitions exposed outside a raw PATCH
 * (tenants/routes.ts:330-362); both require confirmation since they have real
 * side effects (activate flips the unit occupied and bills the first prorated
 * invoice; terminate is the tenant's move-out).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, FileText, Pencil, Trash2, XCircle } from 'lucide-react';
import { parseApiError } from '../../lib/apiError';
import { useIsMobile } from '../../lib/useIsMobile';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EmptyState } from '../../components/EmptyState';
import { activateLease, deleteLease, listLeases, terminateLease } from '../../api/endpoints/tenants';

export function LeaseListPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<{
    id: number;
    kind: 'activate' | 'terminate' | 'delete';
  } | null>(null);

  const query = useQuery({
    queryKey: ['leases', { page, search }],
    queryFn: () => listLeases({ page, search: search === '' ? undefined : search }),
  });

  const activate = useMutation({
    mutationFn: activateLease,
    onSuccess: () => {
      setActionError(null);
      void queryClient.invalidateQueries({ queryKey: ['leases'] });
    },
    onError: (caught) => setActionError(parseApiError(caught).message),
  });

  const terminate = useMutation({
    mutationFn: terminateLease,
    onSuccess: () => {
      setActionError(null);
      void queryClient.invalidateQueries({ queryKey: ['leases'] });
    },
    onError: (caught) => setActionError(parseApiError(caught).message),
  });

  const remove = useMutation({
    mutationFn: deleteLease,
    onSuccess: () => {
      setActionError(null);
      void queryClient.invalidateQueries({ queryKey: ['leases'] });
    },
    onError: (caught) => setActionError(parseApiError(caught).message),
  });

  const leases = query.data?.results ?? [];
  const hasNext = query.data?.next !== null && query.data?.next !== undefined;
  const hasPrevious = query.data?.previous !== null && query.data?.previous !== undefined;

  return (
    <section>
      <h2>Leases</h2>

      <p>
        <Link to="new">New lease</Link>
      </p>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          setPage(1);
        }}
      >
        <label htmlFor="lease-search">Search</label>
        <input
          id="lease-search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Tenant name or unit number"
        />
        <button type="submit">Search</button>
      </form>

      {actionError !== null && (
        <p role="alert" className="form__error">
          {actionError}
        </p>
      )}

      {query.isPending && <p>Loading…</p>}
      {query.isError && (
        <p role="alert" className="form__error">
          {parseApiError(query.error).message}
        </p>
      )}
      {query.isSuccess && leases.length === 0 && (
        <EmptyState icon={FileText} message="No leases found." action={{ label: 'New lease', to: 'new' }} />
      )}

      {query.isSuccess && leases.length > 0 && (
        isMobile ? (
          <ul className="record-cards">
            {leases.map((lease) => (
              <li key={lease.id} className="record-card">
                <div className="record-card__row">
                  <span className="record-card__label">Tenant</span>
                  <span className="record-card__value">{lease.tenant_name}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Unit</span>
                  <span className="record-card__value">
                    {lease.unit_number} — {lease.property_name}
                  </span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Term</span>
                  <span className="record-card__value">
                    {lease.start_date ?? '—'} – {lease.end_date ?? '—'}
                  </span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Rent</span>
                  <span className="record-card__value">{lease.rent_amount}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Status</span>
                  <span className="record-card__value">{lease.status}</span>
                </div>
                <div className="record-card__actions">
                  {lease.status === 'PENDING' && (
                    <button
                      type="button"
                      disabled={activate.isPending}
                      onClick={() => setPendingAction({ id: lease.id, kind: 'activate' })}
                    >
                      <CheckCircle2 size={14} className="icon-inline" aria-hidden="true" />
                      Activate
                    </button>
                  )}
                  {lease.status === 'ACTIVE' && (
                    <button
                      type="button"
                      disabled={terminate.isPending}
                      onClick={() => setPendingAction({ id: lease.id, kind: 'terminate' })}
                    >
                      <XCircle size={14} className="icon-inline" aria-hidden="true" />
                      Terminate
                    </button>
                  )}
                  <Link to={`${lease.id}/edit`} className="button button--outlined">
                    <Pencil size={14} className="icon-inline" aria-hidden="true" />
                    Edit
                  </Link>
                  <button
                    type="button"
                    className="button--danger"
                    disabled={remove.isPending}
                    onClick={() => setPendingAction({ id: lease.id, kind: 'delete' })}
                  >
                    <Trash2 size={14} className="icon-inline" aria-hidden="true" />
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Unit</th>
                <th>Property</th>
                <th>Start</th>
                <th>End</th>
                <th>Rent</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {leases.map((lease) => (
                <tr key={lease.id}>
                  <td>{lease.tenant_name}</td>
                  <td>{lease.unit_number}</td>
                  <td>{lease.property_name}</td>
                  <td>{lease.start_date ?? '—'}</td>
                  <td>{lease.end_date ?? '—'}</td>
                  <td>{lease.rent_amount}</td>
                  <td>{lease.status}</td>
                  <td>
                    <span className="row-actions">
                      {lease.status === 'PENDING' && (
                        <button
                          type="button"
                          disabled={activate.isPending}
                          onClick={() => setPendingAction({ id: lease.id, kind: 'activate' })}
                        >
                          <CheckCircle2 size={14} className="icon-inline" aria-hidden="true" />
                          Activate
                        </button>
                      )}
                      {lease.status === 'ACTIVE' && (
                        <button
                          type="button"
                          disabled={terminate.isPending}
                          onClick={() => setPendingAction({ id: lease.id, kind: 'terminate' })}
                        >
                          <XCircle size={14} className="icon-inline" aria-hidden="true" />
                          Terminate
                        </button>
                      )}
                      <Link to={`${lease.id}/edit`}>
                        <Pencil size={14} className="icon-inline" aria-hidden="true" />
                        Edit
                      </Link>
                      <button
                        type="button"
                        className="button--danger"
                        disabled={remove.isPending}
                        onClick={() => setPendingAction({ id: lease.id, kind: 'delete' })}
                      >
                        <Trash2 size={14} className="icon-inline" aria-hidden="true" />
                        Delete
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      <ConfirmDialog
        open={pendingAction !== null}
        title={
          pendingAction?.kind === 'terminate'
            ? 'Terminate lease'
            : pendingAction?.kind === 'delete'
              ? 'Delete lease'
              : 'Activate lease'
        }
        message={
          pendingAction?.kind === 'terminate'
            ? 'Terminate this lease?'
            : pendingAction?.kind === 'delete'
              ? 'Delete this lease? This cannot be undone.'
              : 'Activate this lease? This marks the unit occupied and bills the first prorated invoice.'
        }
        confirmLabel={
          pendingAction?.kind === 'terminate' ? 'Terminate' : pendingAction?.kind === 'delete' ? 'Delete' : 'Activate'
        }
        tone={pendingAction?.kind === 'terminate' || pendingAction?.kind === 'delete' ? 'danger' : 'default'}
        onConfirm={() => {
          if (pendingAction !== null) {
            if (pendingAction.kind === 'activate') activate.mutate(pendingAction.id);
            else if (pendingAction.kind === 'terminate') terminate.mutate(pendingAction.id);
            else remove.mutate(pendingAction.id);
          }
          setPendingAction(null);
        }}
        onCancel={() => setPendingAction(null)}
      />

      <p>
        <button type="button" disabled={!hasPrevious} onClick={() => setPage((p) => p - 1)}>
          Previous
        </button>{' '}
        <span>Page {page}</span>{' '}
        <button type="button" disabled={!hasNext} onClick={() => setPage((p) => p + 1)}>
          Next
        </button>
      </p>
    </section>
  );
}

export default LeaseListPage;
